import React, { useState, useEffect } from 'react';
import { Icon } from '../../components/Icon';
import { Pagination } from '../../components/ui/Pagination';
import type { Lead } from '../../types/lead';
import { Link } from 'react-router-dom';
import { encryptId } from '../../utils/crypto';
import type { User } from '../../types/lead';

import { formatDateUTC } from '../../utils/formatters';

interface TableProps {
  data: Lead[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onRefresh: () => void;
  bulkAssignLeads: (leadIds: number[], userId: number) => Promise<{ success: boolean, message?: string }>;
  users?: User[];
}

export const Table: React.FC<TableProps> = ({
  data,
  loading,
  currentPage,
  totalPages,
  pageSize,
  total,
  onPageChange,
  onRefresh,
  bulkAssignLeads,
  users = []
}) => {
  const [selectedLeads, setSelectedLeads] = useState<number[]>([]);
  const [showAssignModal, setShowAssignModal] = useState(false); // NUEVO
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null); // NUEVO
  const [isAssigning, setIsAssigning] = useState(false);
  // const [showBulkActions, setShowBulkActions] = useState(false);

  // Resetear selección cuando cambia la página
  useEffect(() => {
    setSelectedLeads([]);
  }, [currentPage]);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedLeads(data.map(lead => lead.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId: number) => {
    setSelectedLeads(prev => {
      if (prev.includes(leadId)) {
        return prev.filter(id => id !== leadId);
      } else {
        return [...prev, leadId];
      }
    });
  };

  const isAllSelected = data.length > 0 && selectedLeads.length === data.length;
  const isSomeSelected = selectedLeads.length > 0 && selectedLeads.length < data.length;

  // const handleBulkStatusChange = () => {

  //   // Implementar lógica aquí
  // };

  // Reemplazar handleBulkAssign con:
  const handleBulkAssign = () => {
    setShowAssignModal(true);
  };

  // NUEVO - Confirmar asignación
  const confirmBulkAssign = async () => {
    if (!selectedUserId) return;
    
    setIsAssigning(true);
    try {
      await bulkAssignLeads(selectedLeads, selectedUserId);
      setSelectedLeads([]);
      setShowAssignModal(false);
      setSelectedUserId(null);
      onRefresh(); // Recargar datos
      // Opcional: mostrar toast de éxito
    } catch (error) {
      console.error('Error asignando leads:', error);
      // Opcional: mostrar toast de error
    } finally {
      setIsAssigning(false);
    }
  };

  // const handleBulkDelete = () => {
  //   if (window.confirm(`¿Eliminar ${selectedLeads.length} leads seleccionados?`)) {

  //     // Implementar lógica aquí
  //   }
  // };

  return (
    <div className="card p-1 lg:p-4">
      {/* Barra de acciones masivas */}
      {selectedLeads.length > 0 && (
        <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center space-x-2">
              <Icon name="check-circle" className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-900 dark:text-blue-300">
                {selectedLeads.length} lead{selectedLeads.length !== 1 ? 's' : ''} seleccionado{selectedLeads.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {/* <button
                onClick={handleBulkStatusChange}
                className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center space-x-1"
              >
                <Icon name="refresh-cw" className="w-4 h-4" />
                <span>Cambiar Status</span>
              </button> */}
              <button
                onClick={handleBulkAssign}
                className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center space-x-1"
              >
                <Icon name="user-plus" className="w-4 h-4" />
                <span>Asignar Agente</span>
              </button>
              {/* <button
                onClick={handleBulkDelete}
                className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center space-x-1"
              >
                <Icon name="trash" className="w-4 h-4" />
                <span>Eliminar</span>
              </button> */}
              <button
                onClick={() => setSelectedLeads([])}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

       {/* NUEVO - Modal de asignación */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/40 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Asignar {selectedLeads.length} lead{selectedLeads.length !== 1 ? 's' : ''}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Seleccionar usuario
              </label>
              <select
                value={selectedUserId || ''}
                onChange={(e) => setSelectedUserId(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Seleccionar --</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.full_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedUserId(null);
                }}
                disabled={isAssigning}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={confirmBulkAssign}
                disabled={!selectedUserId || isAssigning}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-lg transition-colors flex items-center space-x-2"
              >
                {isAssigning && <Icon name="loading" className="w-4 h-4 animate-spin" />}
                <span>{isAssigning ? 'Asignando...' : 'Confirmar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border border-gray-200">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(input) => {
                    if (input) {
                      input.indeterminate = isSomeSelected;
                    }
                  }}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Modificado
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Nombre
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Telefono
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Ciudad
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Programa de Interés
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Fuente
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Etapa
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Asignado
              </th>
              {/* <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Acciones
              </th> */}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={12} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <Icon name="loading" className="h-8 w-8 text-primary animate-spin" />
                    <span className="text-gray-600 dark:text-gray-300">Cargando leads...</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={12} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <Icon name="users" className="w-12 h-12 text-gray-400" />
                    <span className="text-gray-500 dark:text-gray-400 text-lg">
                      No se encontraron leads
                    </span>
                    <span className="text-gray-400 dark:text-gray-500 text-sm">
                      Intenta ajustar los filtros de búsqueda
                    </span>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((lead) => (
                <tr
                  key={lead.id}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 ${
                    lead.user?.full_name  ? 'bg-blue-100 dark:bg-blue-800/50' : ''
                  }`}
                >
                  <td className="px-6 py-2 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedLeads.includes(lead.id)}
                      onChange={() => handleSelectLead(lead.id)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                  </td>
                  <td className="px-6 py-2 whitespace-nowrap">
                    <div className="text-xs font-medium text-gray-900 dark:text-white">
                      {formatDateUTC(lead.updated_at)}
                    </div>
                  </td>
                  <td className="px-6 py-2 whitespace-nowrap">
                    <div className="text-xs font-medium text-gray-900 dark:text-white">
                      <Link
                        to={`/admin/leads/detail/${encryptId(lead.id)}`}
                        className="text-blue-600 hover:underline"
                      >
                        {lead?.first_name || ''} {lead?.last_name || ''}
                      </Link>
                    </div>
                  </td>
                  <td className="px-6 py-2 whitespace-nowrap">
                    <div className={`text-xs ${lead?.phone ? "text-gray-900" : "text-gray-300"} dark:text-white`}>
                      {lead?.phone || 'no registrado'}
                    </div>
                  </td>
                  <td className="px-6 py-2 whitespace-nowrap">
                    <div className={`text-xs ${lead?.email ? "text-gray-900" : "text-gray-300"} dark:text-white`}>
                      {lead?.email || 'no registrado'}
                    </div>
                  </td>
                  <td className="px-6 py-2 whitespace-nowrap">
                    <div className={`text-xs ${lead?.city ? "text-gray-900" : "text-gray-300"} dark:text-white`}>
                      {lead?.city || 'no registrado'}
                    </div>
                  </td>
                  <td className="px-6 py-2 whitespace-nowrap">
                    <div className={`text-xs ${lead?.programa?.name ? "text-gray-900" : "text-gray-300"} dark:text-white`}>
                      {lead?.programa?.name || 'no registrado'}
                    </div>
                  </td>
                  <td className="px-6 py-2 whitespace-nowrap">
                    <div className="text-xs text-gray-900 dark:text-white">
                      {lead.source?.source || ""}
                    </div>
                  </td>
                  <td className="px-6 py-2 whitespace-nowrap">
                    <div className="text-xs text-gray-900 dark:text-white">
                      {lead?.process?.stage || ""}
                    </div>
                  </td>
                  <td className="px-6 py-2 whitespace-nowrap">
                    <div className="text-xs text-gray-900 dark:text-white">
                      {lead?.status?.status || "N/A"}
                    </div>
                  </td>
                  <td className="px-6 py-2 whitespace-nowrap">
                    <div className={`text-xs ${lead.user?.full_name ? "text-gray-900" : "text-red-300"} dark:text-white`}>
                      {lead?.user?.full_name || "sin asignar"}
                    </div>
                  </td>
                  {/* <td className="px-6 py-2 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center justify-center space-x-1">
                      <button
                        className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Icon name="trash" className="w-4 h-4" />
                      </button>
                    </div>
                  </td> */}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            total={total}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};